import axios, { AxiosInstance, AxiosStatic } from "axios";
import { JwtTokenFn } from "@me8eon/authentication";
import { LLM_MODEL, AZURE_API_VERSION } from "@me8eon/config";
import { Message } from "../ai.types";


type THeaders = {
    "Content-Type": string;
    Authorization?: string;
    "x-proxy"?: string;
    "x-proxy-token"?: string;
    "Api-Key"?: string;
    "Ocp-Apim-Subscription-Key"?: string;
    accept?: string;
}

export type AzureAiConfig = {
    axios: AxiosStatic;
    baseURL?: string;
    model?: string;
    customisation?: Record<string, any>;
    debug?: boolean;
    useApiKey?: boolean;
    apiVersion?: string;

};

export const defaultAzureAiConfig = (baseURL: string): AzureAiConfig => ({
    axios,
    baseURL,
    model: LLM_MODEL,
    apiVersion: AZURE_API_VERSION,
});

export interface TAzureAiClientConstructor {
    config: AzureAiConfig;
    jwtTokenFn: JwtTokenFn;
    useApiKey?: boolean;
}

export interface AzureAiClientInterface {
    sendRequest(messages: Message[], otherConfig?: any): Promise<Message[]>;
}

class AzureAiClientBase implements AzureAiClientInterface {
    protected axiosInstance: AxiosInstance;
    protected config: AzureAiConfig;
    protected jwtTokenFn: JwtTokenFn;
    protected useApiKey: boolean;
    protected aiModel: string;
    protected apiVersion: string;

    constructor({ config, jwtTokenFn, useApiKey }: TAzureAiClientConstructor) {
        this.config = config;
        this.jwtTokenFn = jwtTokenFn;
        this.useApiKey = useApiKey || false;
        this.axiosInstance = axios.create({ baseURL: config.baseURL });
        this.aiModel = config.model || LLM_MODEL;
        this.apiVersion = config.apiVersion || AZURE_API_VERSION;
    }

    protected async getHeaders(): Promise<THeaders> {
        const jwtToken = await this.jwtTokenFn();
        const headers: THeaders = {
            "Content-Type": "application/json",
            accept: "*/*",
        };
        if (this.useApiKey) {
            headers["Api-Key"] = jwtToken;
            headers["Ocp-Apim-Subscription-Key"] = jwtToken;
        }
        if (!this.useApiKey) {
            // backend-node-v2's /service/completions proxy injects the AI gateway credential
            // server-side, so only our own app JWT needs to travel with the request.
            headers["Authorization"] = `Bearer ${jwtToken}`;
        }
        return headers;
    }

    public async sendRequest(messages: Message[], otherConfig?: any): Promise<Message[]> {
        const headers = await this.getHeaders();
        const model = otherConfig?.model || this.config.model || "gpt-5";
        const response = await this.axiosInstance.post(
            `/service/completions`,
            { messages, ...this.config.customisation, ...otherConfig, model: LLM_MODEL },
            { headers },
        );
        return response.data.choices.map((x: any) => x.message);
    }
}


export default AzureAiClientBase;
